import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import {
	type ProtocolCallRequestEventFormSchema,
	protocolCallRequestEventSchema,
} from "@msuite/utils/schemas";
import type { ProtocolCallRequestEventType } from "@msuite/utils/types";
import { FormProvider, useForm } from "react-hook-form";
import { ProtocolCallRequestEventForm } from "@/forms/protocol-call-request-event-form";
import { client } from "@/hono";

/** Props Interface */
interface LogCallRequestEventModalProps extends ModalProps {
	protocolId: string;
	type: ProtocolCallRequestEventType;
}

export function LogCallRequestEventModal({
	isOpen,
	onClose,
	protocolId,
	type,
}: LogCallRequestEventModalProps) {
	/** Hooks */
	const { t } = useTranslation();
	const qc = useQueryClient();
	const methods = useForm({
		resolver: resolver(protocolCallRequestEventSchema),
		defaultValues: {
			type,
			note: null,
		},
	});

	/** Functions */
	async function handleOnSubmit(json: ProtocolCallRequestEventFormSchema) {
		const response = await client.protocols[":protocolId"]["call-request-events"].$post({
			param: { protocolId },
			json,
		});
		await responseHandler(response);
		await qc.invalidateQueries({ predicate: invalidate(["protocol", protocolId]) });
		onClose();
	}

	/** Computed */
	const title =
		type === "resolved" ? t("protocols.mark_as_called_back") : t("protocols.log_failed_attempt");

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				title={title}
				isOpen={isOpen}
				onClose={onClose}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
				isLoading={methods.formState.isLoading}
			>
				<Modal.Body>
					<ProtocolCallRequestEventForm />
				</Modal.Body>
				<Modal.FooterForm />
			</Modal>
		</FormProvider>
	);
}
